import { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import CountdownBanner from "../components/CountdownBanner";
import RaceCard from "../components/RaceCard";
import LockedInCelebration from "../components/LockedInCelebration";
import Skeleton from "../components/Skeleton";
import { useAuth } from "../context/AuthContext";
import { useLivePoll } from "../lib/useLivePoll";
import { CalendarDays, ChevronLeft, ChevronRight, LayoutGrid, Focus, Check } from "lucide-react";

export default function RacesTab({ group }) {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dates, setDates] = useState([]);
  const [activeDate, setActiveDate] = useState(null);
  const [races, setRaces] = useState([]);
  const [cutoff, setCutoff] = useState(null);
  const [cutoffPassed, setCutoffPassed] = useState(false);
  const [myPicks, setMyPicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [celebrateOpen, setCelebrateOpen] = useState(false);
  const modeStorageKey = user?.id ? `ht_races_mode_v1:${user.id}` : null;
  const [mode, setMode] = useState(() => {
    if (searchParams.get("race")) return "focus";
    if (modeStorageKey) {
      try {
        const stored = localStorage.getItem(modeStorageKey);
        if (stored === "overview" || stored === "focus") return stored;
      } catch (_) { /* localStorage unavailable */ }
    }
    return "focus"; // Default: single-race Focus Pick experience
  });

  const setModePersisted = useCallback((next) => {
    setMode(next);
    if (modeStorageKey) {
      try { localStorage.setItem(modeStorageKey, next); } catch (_) { /* ignore */ }
    }
  }, [modeStorageKey]);

  const loadDates = useCallback(async () => {
    if (!group) return;
    try {
      const { data } = await api.get(`/groups/${group.id}/meeting-dates`);
      setDates(data.meeting_dates);
      setActiveDate((cur) => {
        if (cur && data.meeting_dates.find((d) => d.meeting_date === cur)) return cur;
        // Prefer nearest future date, else last available
        const upcoming = data.meeting_dates.find((d) => !d.cutoff_passed);
        return (upcoming || data.meeting_dates[data.meeting_dates.length - 1])?.meeting_date || null;
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Failed to load dates"));
    }
  }, [group]);

  const loadRaces = useCallback(async () => {
    if (!group || !activeDate) { setRaces([]); setLoading(false); return; }
    setLoading(true);
    try {
      const [{ data: rd }, { data: pd }] = await Promise.all([
        api.get(`/groups/${group.id}/races`, { params: { date: activeDate } }),
        api.get(`/groups/${group.id}/picks/me`, { params: { date: activeDate } }).catch(() => ({ data: { picks: [] } })),
      ]);
      setRaces(rd.races);
      setCutoff(rd.cutoff);
      setCutoffPassed(rd.cutoff_passed);
      setMyPicks(pd.picks || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Failed to load races"));
    } finally {
      setLoading(false);
    }
  }, [group, activeDate]);

  useEffect(() => { loadDates(); }, [loadDates]);
  useEffect(() => { loadRaces(); }, [loadRaces]);
  useLivePoll(loadRaces, 15000, [group?.id, activeDate], { immediate: false });

  const pickByRaceId = useMemo(() => {
    const m = new Map();
    myPicks.forEach((p) => m.set(p.race_id, p));
    return m;
  }, [myPicks]);

  const stagedCount = myPicks.filter((p) => p.status === "staged").length;
  const totalRaces = races.length;

  const stageOnRace = async (race, horse) => {
    if (cutoffPassed || race.status === "running" || race.status === "finished") return;
    try {
      const { data } = await api.post(`/groups/${group.id}/races/${race.id}/picks/stage`, {
        horse_number: horse.horse_number,
      });
      setMyPicks((prev) => {
        const rest = prev.filter((p) => p.race_id !== race.id);
        return [...rest, data.pick];
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Could not stage pick"));
    }
  };

  const confirmAll = async () => {
    setConfirming(true);
    try {
      const { data } = await api.post(`/groups/${group.id}/meeting-dates/${activeDate}/picks/confirm`);
      toast.success(`Confirmed ${data.confirmed} pick${data.confirmed === 1 ? "" : "s"}`);
      await loadRaces();
      if (data.confirmed > 0) setCelebrateOpen(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Confirm failed"));
    } finally {
      setConfirming(false);
    }
  };

  if (!group) return null;

  return (
    <div className="px-4 pt-4 space-y-4 pb-32" data-testid="races-tab-content">
      {/* Countdown */}
      <CountdownBanner cutoffIso={cutoff} meetingDate={activeDate} />

      {/* Date chips */}
      {dates.length > 0 && (
        <div className="flex gap-2 overflow-x-auto -mx-4 px-4" data-testid="races-date-chips">
          {dates.map((d) => {
            const active = d.meeting_date === activeDate;
            return (
              <button
                key={d.meeting_date}
                onClick={() => setActiveDate(d.meeting_date)}
                data-testid={`date-chip-${d.meeting_date}`}
                className={`shrink-0 px-3 py-1.5 rounded-full font-bold text-xs border transition-transform active:scale-95 ${
                  active
                    ? "bg-[#1971c2] text-white border-[#1971c2]"
                    : "bg-white text-[#212529] border-[#e9ecef] hover:border-[#1971c2]"
                }`}
              >
                <CalendarDays className="w-3.5 h-3.5 inline mr-1 -mt-0.5" strokeWidth={2.5} />
                {d.meeting_date} · {d.race_count}
              </button>
            );
          })}
        </div>
      )}

      {/* Mode toggle */}
      {races.length > 0 && (
        <div className="flex bg-white border border-[#e9ecef] rounded-full p-1 self-start" data-testid="races-mode-toggle">
          <button
            onClick={() => { setModePersisted("overview"); const p = new URLSearchParams(searchParams); p.delete("race"); setSearchParams(p, { replace: true }); }}
            data-testid="races-mode-overview"
            className={`px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1 transition-colors ${mode === "overview" ? "bg-[#1971c2] text-white" : "text-[#6c757d]"}`}
          >
            <LayoutGrid className="w-3.5 h-3.5" strokeWidth={2.5} /> Overview
          </button>
          <button
            onClick={() => { setModePersisted("focus"); const p = new URLSearchParams(searchParams); if (races[0]) p.set("race", races[0].id); setSearchParams(p, { replace: true }); }}
            data-testid="races-mode-focus"
            className={`px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1 transition-colors ${mode === "focus" ? "bg-[#1971c2] text-white" : "text-[#6c757d]"}`}
          >
            <Focus className="w-3.5 h-3.5" strokeWidth={2.5} /> Focus pick
          </button>
        </div>
      )}

      {/* Races */}
      {loading ? (
        <Skeleton rows={3} height={200} testId="races-skeleton" />
      ) : races.length === 0 ? (
        <div className="bg-white border border-[#e9ecef] rounded-2xl p-8 text-center" data-testid="races-empty">
          <div className="text-5xl mb-3">🏇</div>
          <div className="font-display font-extrabold text-xl">No races loaded</div>
          <p className="text-sm text-[#6c757d] mt-2 max-w-xs mx-auto">
            An admin needs to load a race card. Head to the Admin tab.
          </p>
        </div>
      ) : (
        mode === "focus" ? (
          <FocusPickView
            races={races}
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            pickByRaceId={pickByRaceId}
            cutoffPassed={cutoffPassed}
            stageOnRace={stageOnRace}
            confirmAll={confirmAll}
            stagedCount={stagedCount}
            confirming={confirming}
            onExitFocus={() => { setModePersisted("overview"); const p = new URLSearchParams(searchParams); p.delete("race"); setSearchParams(p, { replace: true }); }}
          />
        ) : (
          <div className="space-y-4">
            {races.map((r) => (
              <div key={r.id} className="relative">
                <RaceCard
                  race={r}
                  myPick={pickByRaceId.get(r.id)}
                  staged={pickByRaceId.get(r.id)?.status === "staged"}
                  cutoffPassed={cutoffPassed}
                  onPick={stageOnRace}
                />
                {!cutoffPassed && (
                  <button
                    onClick={() => { setModePersisted("focus"); const p = new URLSearchParams(searchParams); p.set("race", r.id); setSearchParams(p, { replace: true }); }}
                    data-testid={`jump-focus-${r.id}`}
                    className="absolute top-3 right-3 rounded-full bg-white/90 border border-[#e9ecef] hover:bg-white text-[#1971c2] font-bold text-[10px] px-2 py-1 flex items-center gap-1 shadow-sm"
                  >
                    <Focus className="w-3 h-3" strokeWidth={2.5} /> Focus
                  </button>
                )}
              </div>
            ))}
          </div>
        )
      )}

      {/* Sticky footer — only in overview mode (focus has its own) */}
      {races.length > 0 && mode === "overview" && (
        <div className="fixed bottom-20 inset-x-0 z-40 pointer-events-none" data-testid="races-footer">
          <div className="max-w-[480px] mx-auto px-4 pb-3 pointer-events-auto">
            <div className="bg-white border border-[#e9ecef] rounded-2xl p-3 flex items-center gap-3 shadow-[0_-4px_20px_-4px_rgba(0,0,0,0.1)]">
              <div className="text-xs font-bold text-[#6c757d] flex-1">
                <span className="font-display font-black text-lg text-[#212529] tabular-nums">
                  {myPicks.length}
                </span>
                /{totalRaces} picked
                {stagedCount > 0 && (
                  <span className="ml-1 text-[#6741d9] font-black">· {stagedCount} pending</span>
                )}
              </div>
              <button
                disabled={stagedCount === 0 || cutoffPassed || confirming}
                onClick={confirmAll}
                data-testid="confirm-all-picks-button"
                className="bg-[#1971c2] hover:bg-[#1864ab] text-white font-display font-extrabold rounded-xl px-5 py-2.5 disabled:opacity-40 disabled:cursor-not-allowed active:scale-95 transition-transform"
              >
                {cutoffPassed ? "Locked" : confirming ? "Confirming…" : "Confirm all picks"}
              </button>
            </div>
          </div>
        </div>
      )}

      <LockedInCelebration
        open={celebrateOpen}
        onClose={() => setCelebrateOpen(false)}
        meetingDate={activeDate}
        groupName={group?.name}
        user={user}
        picks={myPicks.filter((p) => p.status === "confirmed")}
        races={races}
      />
    </div>
  );
}

function FocusPickView({ races, searchParams, setSearchParams, pickByRaceId, cutoffPassed, stageOnRace, confirmAll, stagedCount, confirming, onExitFocus }) {
  const currentId = searchParams.get("race");
  const idx = Math.max(0, races.findIndex((r) => r.id === currentId));
  const race = races[idx] || races[0];
  const isLast = idx === races.length - 1;
  const isFirst = idx === 0;
  const pick = pickByRaceId.get(race.id);
  const hasPick = !!pick;

  const goTo = (i) => {
    const r = races[i];
    if (!r) return;
    const p = new URLSearchParams(searchParams);
    p.set("race", r.id);
    setSearchParams(p, { replace: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div data-testid="focus-pick-view">
      {/* Sticky race counter */}
      <div className="sticky top-16 z-40 -mx-4 px-4 py-2 bg-[#f8f9fa]/95 backdrop-blur border-b border-[#e9ecef] flex items-center gap-2" data-testid="focus-race-counter">
        <div className="flex-1 min-w-0">
          <div className="font-display font-black text-sm truncate">
            Race {idx + 1} of {races.length} · {race.track} R{race.race_number}
          </div>
          <div className="flex gap-1 mt-1" aria-hidden>
            {races.map((r, i) => {
              const hp = pickByRaceId.has(r.id);
              return (
                <button
                  key={r.id}
                  onClick={() => goTo(i)}
                  className={`h-1.5 flex-1 rounded-full transition-colors ${i === idx ? "bg-[#1971c2]" : hp ? "bg-[#2f9e44]" : "bg-[#e9ecef]"}`}
                  aria-label={`Go to race ${i + 1}`}
                  data-testid={`focus-progress-dot-${i}`}
                />
              );
            })}
          </div>
        </div>
      </div>

      <div className="pt-3">
        <RaceCard
          race={race}
          myPick={pick}
          staged={pick?.status === "staged"}
          cutoffPassed={cutoffPassed}
          onPick={stageOnRace}
        />
      </div>

      {/* Focus action bar */}
      <div className="fixed bottom-20 inset-x-0 z-40 pointer-events-none">
        <div className="max-w-[480px] mx-auto px-4 pb-3 pointer-events-auto">
          <div className="bg-white border border-[#e9ecef] rounded-2xl p-2 flex items-center gap-2 shadow-[0_-4px_20px_-4px_rgba(0,0,0,0.1)]">
            <button
              onClick={() => goTo(idx - 1)}
              disabled={isFirst}
              data-testid="focus-prev"
              className="min-h-[44px] px-3 rounded-xl bg-[#f8f9fa] hover:bg-[#e9ecef] font-bold text-sm flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed active:scale-95 transition-transform"
            >
              <ChevronLeft className="w-4 h-4" strokeWidth={2.5} /> Prev
            </button>
            {cutoffPassed ? (
              <button
                onClick={onExitFocus}
                data-testid="focus-back-to-overview"
                className="flex-1 min-h-[44px] bg-[#1971c2] text-white font-display font-extrabold rounded-xl px-3 active:scale-95"
              >
                Back to overview
              </button>
            ) : isLast ? (
              <button
                onClick={confirmAll}
                disabled={stagedCount === 0 || confirming}
                data-testid="focus-confirm-all"
                className="flex-1 min-h-[44px] bg-[#2fb344] hover:bg-[#2f9e44] text-white font-display font-extrabold rounded-xl px-3 disabled:opacity-40 disabled:cursor-not-allowed active:scale-95 transition-transform"
              >
                {confirming ? "Confirming…" : `Confirm all picks (${stagedCount})`}
              </button>
            ) : (
              <button
                onClick={() => goTo(idx + 1)}
                data-testid="focus-next"
                className="flex-1 min-h-[44px] bg-[#1971c2] hover:bg-[#1864ab] text-white font-display font-extrabold rounded-xl px-3 flex items-center justify-center gap-1 active:scale-95 transition-transform"
              >
                {hasPick && <Check className="w-4 h-4" strokeWidth={3} />}
                Next race
                <ChevronRight className="w-4 h-4" strokeWidth={2.5} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
